import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
    { to: "/", label: "Home", id: "home" },
    { to: "/services", label: "Services", id: "services" },
    { to: "/work", label: "Work", id: "work" },
    { to: "/pricing", label: "Pricing", id: "pricing" },
    { to: "/faq", label: "FAQ", id: "faq" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="main-nav"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
                scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-10">
                <Link to="/" data-testid="nav-logo" className="flex items-center gap-4 group">
                    <img
                        src="/assets/camarca-logo.png"
                        alt="Camarca Digital Designs logo"
                        className="h-16 w-16 rounded-full transition-transform duration-500 group-hover:rotate-12"
                    />
                    <span className="leading-none">
                        <span className="block font-display text-base font-bold tracking-wide text-white">CAMARCA</span>
                        <span className="mt-1 block font-mono text-[10px] tracking-[0.3em] text-zinc-500">DIGITAL DESIGNS</span>
                    </span>
                </Link>

                <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
                    {links.map((l) => (
                        <NavLink
                            key={l.id}
                            to={l.to}
                            data-testid={`nav-link-${l.id}`}
                            className={({ isActive }) =>
                                `group relative font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                                    isActive ? "text-neon" : "text-zinc-400 hover:text-white"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {l.label}
                                    <span
                                        className={`absolute -bottom-1.5 left-0 h-px bg-neon transition-[width] duration-300 ${
                                            isActive ? "w-full" : "w-0 group-hover:w-full"
                                        }`}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                    <Link
                        to="/contact"
                        data-testid="nav-quote-button"
                        className="group flex items-center gap-2 border border-neon/70 px-5 py-2.5 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color,box-shadow] duration-300 hover:bg-neon hover:text-void hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
                    >
                        Request a Quote
                        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </nav>

                <button
                    data-testid="mobile-menu-button"
                    onClick={() => setOpen(!open)}
                    className="text-white lg:hidden"
                    aria-label={open ? "Close menu" : "Open menu"}
                >
                    {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.3 }}
                        className="border-b border-white/10 bg-void/95 backdrop-blur-xl lg:hidden"
                        aria-label="Mobile"
                    >
                        <div className="flex flex-col gap-1 px-6 py-6">
                            {[...links, { to: "/contact", label: "Request a Quote", id: "contact" }].map((l, i) => (
                                <motion.div
                                    key={l.id}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                >
                                    <NavLink
                                        to={l.to}
                                        data-testid={`mobile-nav-link-${l.id}`}
                                        className={({ isActive }) =>
                                            `block py-3 font-display text-2xl font-bold transition-colors duration-300 ${
                                                isActive ? "text-neon" : "text-white hover:text-neon"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
